import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  effect,
  input,
  inject,
  ChangeDetectorRef,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  HealthcareSqlPlaygroundService,
  FileTypeDescriptor,
} from './sql-playground.service';
import { FileApiService } from '@angular-workspace/services';
import { FormatService } from '../../../shared/services/format.service';

export interface VersionFile {
  name: string;
  size?: string;
  lastUpdated?: string;
  license?: string;
  isSelected?: boolean;
}

const SQL_PLAYGROUND_EXTENSIONS = ['json', 'csv', 'tsv', 'parquet'];

@Component({
  selector: 'app-dataset-detail-sql-playground-tab',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './sql-playground-tab.component.html',
  styleUrl: './sql-playground-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatasetDetailSqlPlaygroundTabComponent implements OnInit {
  readonly datasetId = input<string | null>(null);
  readonly license = input<string>('');

  displayFiles: VersionFile[] = [];
  displayFilesTotalSize = '';
  displayFilesLastUpdated = '';
  loadingFiles = false;
  filesError: string | null = null;

  sql = '';
  editorHeight = 180;
  editorFontSize = 14;
  highlightedSql: SafeHtml = '';
  isRunning = false;
  runStatus: 'idle' | 'running' | 'success' | 'error' = 'idle';
  lastRunAt: Date | null = null;
  executionTimeMs: number | null = null;
  resultColumns: string[] = [];
  resultRows: Record<string, unknown>[] = [];
  errorMessage: string | null = null;

  supportedFileTypes: FileTypeDescriptor[] = [];
  fileInputAccept = '';
  isUploading = false;
  uploadError: string | null = null;
  customFiles: Array<{ name: string; size: string; isSelected: boolean; tableName?: string }> = [];
  activeTableNames: string[] = [];
  activeFileNames: string[] = [];
  expandedFileDetails: string | null = null;
  versionFilesTooltipVisible = false;
  versionFilesTooltipTop = 0;
  versionFilesTooltipLeft = 0;
  /** Maps version file name -> DuckDB table name after load */
  loadedVersionFiles = new Map<string, string>();
  /** File name currently being loaded from dataset API */
  loadingVersionFileName: string | null = null;

  private sqlPlaygroundService = inject(HealthcareSqlPlaygroundService);
  private sanitizer = inject(DomSanitizer);
  private fileApiService = inject(FileApiService);
  private formatService = inject(FormatService);
  private cdr = inject(ChangeDetectorRef);

  private loadGeneration = 0;

  constructor() {
    effect(() => {
      const id = this.datasetId();
      const gen = ++this.loadGeneration;
      untracked(() => {
        void this.onDatasetIdChange(id, gen);
      });
    });
  }

  canResetPlayground(): boolean {
    return (
      !!this.sql?.trim() ||
      this.runStatus !== 'idle' ||
      this.resultRows.length > 0 ||
      this.resultColumns.length > 0 ||
      this.errorMessage !== null ||
      this.uploadError !== null ||
      this.executionTimeMs !== null ||
      this.lastRunAt !== null
    );
  }

  hasSelectedFiles(): boolean {
    return (
      this.activeTableNames.length > 0 ||
      this.displayFiles.some((f) => f.isSelected) ||
      this.customFiles.some((f) => f.isSelected)
    );
  }

  getLastRunDisplay(): string {
    if (!this.lastRunAt) return '';
    return this.formatService.formatDate(this.lastRunAt.toISOString());
  }

  ngOnInit(): void {
    this.supportedFileTypes = this.sqlPlaygroundService.getSupportedFileTypes();
    this.fileInputAccept = this.supportedFileTypes
      .flatMap((t) => t.extensions.map((ext) => `.${ext}`))
      .join(',');
    this.sql = '';
    this.highlightedSql = this.sanitizer.bypassSecurityTrustHtml(this.highlightSql(this.sql));
  }

  private async onDatasetIdChange(id: string | null, gen: number): Promise<void> {
    this.loadedVersionFiles.clear();
    this.activeTableNames = [];
    this.activeFileNames = [];
    this.displayFiles = [];
    this.displayFilesTotalSize = '';
    this.displayFilesLastUpdated = '';
    this.filesError = null;
    if (!id) {
      this.cdr.markForCheck();
      return;
    }
    await this.loadFilesFromApi(id, gen);
  }

  private isSqlPlaygroundFile(fileName: string): boolean {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    return SQL_PLAYGROUND_EXTENSIONS.includes(ext);
  }

  private async loadFilesFromApi(id: string, gen: number): Promise<void> {
    this.loadingFiles = true;
    this.filesError = null;
    this.cdr.markForCheck();
    try {
      const response = await this.fileApiService.getFilesForPreview(id);
      if (gen !== this.loadGeneration) return;
      const apiFiles = this.extractFilesFromResponse(response?.data) as Array<{
        key?: string;
        size?: number;
        lastModified?: string;
        isFile?: boolean;
      }>;
      if (apiFiles.length > 0) {
        const filtered = apiFiles.filter(
          (f) => f.isFile !== false && this.isSqlPlaygroundFile(f.key || '')
        );
        let totalSizeBytes = 0;
        let latestModified: Date | null = null;
        for (const f of filtered) {
          totalSizeBytes += f.size || 0;
          if (f.lastModified) {
            const d = new Date(f.lastModified);
            if (!latestModified || d > latestModified) latestModified = d;
          }
        }
        this.displayFiles = filtered.map((f, i) => ({
          name: f.key || `file_${i + 1}`,
          size: (f.size || 0) > 0 ? this.formatFileSize(f.size!) : undefined,
          lastUpdated: f.lastModified,
          license: this.license(),
          isSelected: i === 0,
        }));
        this.displayFilesTotalSize =
          totalSizeBytes > 0 ? this.formatFileSize(totalSizeBytes) : '';
        this.displayFilesLastUpdated = latestModified
          ? this.formatService.formatDate(latestModified.toISOString()) ||
            latestModified.toLocaleDateString()
          : '';
      } else {
        this.displayFiles = [];
        this.displayFilesTotalSize = '';
        this.displayFilesLastUpdated = '';
      }
    } catch (err: unknown) {
      if (gen === this.loadGeneration) {
        this.filesError = err instanceof Error ? err.message : 'Failed to load files';
        this.displayFiles = [];
      }
      console.error('[SQL Playground] Error loading files:', err);
    } finally {
      if (gen === this.loadGeneration) this.loadingFiles = false;
      this.cdr.markForCheck();
    }
  }

  private extractFilesFromResponse(data: unknown): unknown[] {
    if (!data || typeof data !== 'object') return [];
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj['files'])) return obj['files'];
    const inner = obj['data'] as Record<string, unknown> | undefined;
    if (inner && typeof inner === 'object' && Array.isArray(inner['files'])) return inner['files'];
    return [];
  }

  async runQuery(): Promise<void> {
    if (this.isRunning) return;
    this.isRunning = true;
    this.runStatus = 'running';
    this.executionTimeMs = null;
    this.resultColumns = [];
    this.resultRows = [];
    this.errorMessage = null;
    try {
      const result = await this.sqlPlaygroundService.executeQuery(this.sql);
      this.resultRows = result.rows;
      this.resultColumns = result.columns;
      this.executionTimeMs = result.executionTimeMs;
      this.lastRunAt = new Date();
      this.runStatus = 'success';
    } catch (error) {
      this.runStatus = 'error';
      this.executionTimeMs = null;
      this.resultRows = [];
      this.resultColumns = [];
      this.errorMessage = this.extractErrorMessage(error);
      console.error('[SQL Playground] Query execution failed:', error);
    } finally {
      this.isRunning = false;
    }
  }

  resetPlayground(): void {
    if (this.isRunning) return;
    this.sql = '';
    this.highlightedSql = this.sanitizer.bypassSecurityTrustHtml(this.highlightSql(this.sql));
    this.runStatus = 'idle';
    this.lastRunAt = null;
    this.executionTimeMs = null;
    this.resultColumns = [];
    this.resultRows = [];
    this.errorMessage = null;
    this.uploadError = null;
  }

  async onFileSelected(event: Event): Promise<void> {
    const inputEl = event.target as HTMLInputElement;
    const file = inputEl.files?.item(0);
    if (!file || this.isUploading) return;
    this.isUploading = true;
    this.uploadError = null;
    try {
      const tableName = this.generateTableNameFromFileName(file.name);
      const { tableName: loadedTableName, fileName } = await this.sqlPlaygroundService.loadFile(
        file,
        tableName
      );
      const fileSize = this.formatFileSize(file.size);
      this.customFiles.push({
        name: file.name,
        size: fileSize,
        isSelected: true,
        tableName: loadedTableName,
      });
      if (!this.activeTableNames.includes(loadedTableName)) this.activeTableNames.push(loadedTableName);
      if (!this.activeFileNames.includes(file.name)) this.activeFileNames.push(file.name);
      this.updateQueryForActiveTables();
      this.cdr.markForCheck();
      await this.runQuery();
    } catch (error) {
      this.uploadError = this.extractErrorMessage(error);
    } finally {
      this.isUploading = false;
      if (inputEl) inputEl.value = '';
      this.cdr.markForCheck();
    }
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  generateTableNameFromFileName(fileName: string): string {
    const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
    const sanitized = nameWithoutExt
      .replace(/[^a-zA-Z0-9_]/g, '_')
      .toLowerCase()
      .replace(/^_+|_+$/g, '')
      .replace(/_+/g, '_');
    if (!sanitized || /^\d/.test(sanitized)) return 'uploaded_data';
    return sanitized || 'uploaded_data';
  }

  onCustomFileToggle(file: {
    name: string;
    size: string;
    isSelected: boolean;
    tableName?: string;
  }): void {
    file.isSelected = !file.isSelected;
    if (file.isSelected && file.tableName) {
      if (!this.activeTableNames.includes(file.tableName)) this.activeTableNames.push(file.tableName);
      if (!this.activeFileNames.includes(file.name)) this.activeFileNames.push(file.name);
    } else {
      if (file.tableName) this.activeTableNames = this.activeTableNames.filter((t) => t !== file.tableName);
      this.activeFileNames = this.activeFileNames.filter((n) => n !== file.name);
    }
    this.updateQueryForActiveTables();
  }

  removeCustomFile(file: { name: string; tableName?: string }): void {
    this.customFiles = this.customFiles.filter((f) => f.name !== file.name);
    if (file.tableName) this.activeTableNames = this.activeTableNames.filter((t) => t !== file.tableName);
    this.activeFileNames = this.activeFileNames.filter((n) => n !== file.name);
    this.updateQueryForActiveTables();
    this.runStatus = 'idle';
    this.lastRunAt = null;
    this.executionTimeMs = null;
    this.resultColumns = [];
    this.resultRows = [];
    this.errorMessage = null;
    this.cdr.markForCheck();
  }

  async onFileCardClick(file: VersionFile): Promise<void> {
    if (file.isSelected) {
      file.isSelected = false;
      const tableName = this.loadedVersionFiles.get(file.name);
      if (tableName) {
        this.activeTableNames = this.activeTableNames.filter((t) => t !== tableName);
      }
      this.activeFileNames = this.activeFileNames.filter((n) => n !== file.name);
      this.updateQueryForActiveTables();
      this.cdr.markForCheck();
      return;
    }

    file.isSelected = true;
    let tableName = this.loadedVersionFiles.get(file.name);
    if (tableName) {
      if (!this.activeTableNames.includes(tableName)) this.activeTableNames.push(tableName);
      if (!this.activeFileNames.includes(file.name)) this.activeFileNames.push(file.name);
      this.updateQueryForActiveTables();
      this.cdr.markForCheck();
      return;
    }

    const id = this.datasetId();
    if (!id) {
      file.isSelected = false;
      this.uploadError = 'Dataset ID is required to load version files.';
      this.cdr.markForCheck();
      return;
    }

    this.loadingVersionFileName = file.name;
    this.uploadError = null;
    this.cdr.markForCheck();
    try {
      const blob = await this.fileApiService.getFileBlob(id, { key: file.name });
      const baseName = this.generateTableNameFromFileName(file.name);
      tableName = `${baseName}_v${Date.now().toString(36)}`;
      const result = await this.sqlPlaygroundService.loadFromBlob(blob, file.name, tableName);
      this.loadedVersionFiles.set(file.name, result.tableName);
      if (!this.activeTableNames.includes(result.tableName)) this.activeTableNames.push(result.tableName);
      if (!this.activeFileNames.includes(file.name)) this.activeFileNames.push(file.name);
      this.updateQueryForActiveTables();
      await this.runQuery();
    } catch (err) {
      file.isSelected = false;
      this.uploadError = this.extractErrorMessage(err);
      console.error('[SQL Playground] Error loading version file:', err);
    } finally {
      this.loadingVersionFileName = null;
      this.cdr.markForCheck();
    }
  }

  private updateQueryForActiveTables(): void {
    const tables = this.activeTableNames.filter((t) => t);
    if (tables.length === 0) {
      this.sql = '';
      this.highlightedSql = this.sanitizer.bypassSecurityTrustHtml(this.highlightSql(this.sql));
      return;
    }
    if (tables.length === 1) {
      this.sql = `SELECT * FROM ${tables[0]} LIMIT 20;`;
    } else {
      let query = `SELECT * FROM ${tables[0]}`;
      for (let i = 1; i < tables.length; i++) query += `\nCROSS JOIN ${tables[i]}`;
      query += `\nLIMIT 20;`;
      this.sql = query;
    }
    this.highlightedSql = this.sanitizer.bypassSecurityTrustHtml(this.highlightSql(this.sql));
  }

  private extractErrorMessage(error: unknown): string {
    if (!error) return 'An unknown error occurred.';
    if (error instanceof Error) return error.message || 'An unexpected error occurred.';
    if (typeof error === 'string') return error;
    try {
      return JSON.stringify(error);
    } catch {
      return 'An unexpected error occurred.';
    }
  }

  toggleFileDetails(fileName: string, context: 'version' | 'custom'): void {
    const key = `${context}:${fileName}`;
    this.expandedFileDetails = this.expandedFileDetails === key ? null : key;
  }

  isFileDetailsExpanded(fileName: string, context: 'version' | 'custom'): boolean {
    return this.expandedFileDetails === `${context}:${fileName}`;
  }

  showVersionFilesTooltip(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.versionFilesTooltipTop = rect.bottom + 8;
    this.versionFilesTooltipLeft = rect.left + rect.width / 2;
    this.versionFilesTooltipVisible = true;
  }

  hideVersionFilesTooltip(): void {
    this.versionFilesTooltipVisible = false;
  }

  increaseFontSize(): void {
    if (this.editorFontSize < 24) this.editorFontSize += 2;
  }

  decreaseFontSize(): void {
    if (this.editorFontSize > 10) this.editorFontSize -= 2;
  }

  onSqlInput(event: Event): void {
    setTimeout(() => {
      this.highlightedSql = this.sanitizer.bypassSecurityTrustHtml(this.highlightSql(this.sql));
    }, 0);
    const textarea = event.target as HTMLTextAreaElement;
    const highlightEl = textarea?.parentElement?.querySelector('.playground-editor-highlight') as HTMLElement;
    if (highlightEl) {
      highlightEl.scrollTop = textarea.scrollTop;
      highlightEl.scrollLeft = textarea.scrollLeft;
    }
  }

  onSqlScroll(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    const highlightEl = textarea.parentElement?.querySelector('.playground-editor-highlight') as HTMLElement;
    if (highlightEl) {
      highlightEl.scrollTop = textarea.scrollTop;
      highlightEl.scrollLeft = textarea.scrollLeft;
    }
  }

  highlightSql(sql: string): string {
    if (!sql) return '';
    let highlighted = sql
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    highlighted = highlighted.replace(/--.*$/gm, '<span class="sql-comment">$&</span>');
    highlighted = highlighted.replace(/\/\*[\s\S]*?\*\//g, '<span class="sql-comment">$&</span>');
    highlighted = highlighted.replace(/'([^']*)'/g, '<span class="sql-string">\'$1\'</span>');
    highlighted = highlighted.replace(/"([^"]*)"/g, '<span class="sql-string">"$1"</span>');
    const keywords = [
      'UNION ALL', 'INNER JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'FULL JOIN',
      'ORDER BY', 'GROUP BY', 'IS NOT NULL', 'IS NULL',
      'SELECT', 'FROM', 'WHERE', 'HAVING', 'INSERT', 'UPDATE', 'DELETE',
      'CREATE', 'ALTER', 'DROP', 'TABLE', 'INDEX', 'VIEW', 'DATABASE', 'SCHEMA',
      'JOIN', 'ON', 'AS', 'AND', 'OR', 'NOT', 'IN', 'LIKE', 'BETWEEN',
      'UNION', 'INTERSECT', 'EXCEPT', 'DISTINCT', 'ALL', 'TOP', 'LIMIT', 'OFFSET',
      'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'COUNT', 'SUM', 'AVG', 'MAX', 'MIN',
      'ASC', 'DESC', 'NULL', 'EXISTS', 'ANY', 'SOME',
    ];
    keywords.forEach((keyword) => {
      const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`\\b(${escaped.replace(/\s+/g, '\\s+')})\\b(?![^<]*>)`, 'gi');
      highlighted = highlighted.replace(re, '<span class="sql-keyword">$1</span>');
    });
    highlighted = highlighted.replace(/\b(\d+\.?\d*)\b(?![^<]*>)/g, '<span class="sql-number">$1</span>');
    return highlighted;
  }
}
